import { EnumChip } from '@/common/card/EnumChip';
import { useContextMenu } from '@/common/contextMenu/useContextMenu';
import { useDialog } from '@/common/dialog/DialogContext';
import { IncCondForm } from '@/common/entities/incCond/IncCondForm';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { EntityCardList } from '@/common/entityFramework/entityCardList/EntityCardList';
import { EntityFormDialog } from '@/common/entityFramework/entityForm/EntityFormDialog';
import { ConfirmDeleteDialog } from '@/common/entityFramework/entityList/ConfirmDeleteDialog';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useEnumDisplayNames } from '@/common/hooks/useEnumDisplayNames';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

const LOCKED_STATUSES = ['APPROVED', 'APPROVING', 'IMPL'];

// One expandable card per Inclusion Rule of a Data Source; the card body lists the Rule's Conditions.
// All add/edit happens in dialogs so the Data Source workspace never navigates away.
export const IncRuleCardList = ({ incDataSrcObj, canEdit = true }) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const { openDialog } = useDialog();
  const { hasRole } = usePrincipal();
  const { navigateTo } = useNavState();
  const { openContextMenu, contextMenu: condContextMenu } = useContextMenu();

  const [highlightId, setHighlightId] = useState(null);
  const [ruleIdToConds, setRuleIdToConds] = useState({});
  const [incListById, setIncListById] = useState({});

  const rolePrefix = module.toUpperCase();
  const canSaveRule = canEdit && hasRole(rolePrefix + '_INC_RULE_SAVE');
  const canDeleteRule = canEdit && hasRole(rolePrefix + '_INC_RULE_DELETE');
  const canSaveCond = canEdit && hasRole(rolePrefix + '_INC_COND_SAVE');
  const canDeleteCond = canEdit && hasRole(rolePrefix + '_INC_COND_DELETE');

  const { getEnumSpec: condEnumSpec } = useEnumDisplayNames(module, 'INC_COND');

  const augRowsReq = useCallback(
    (rowsReq) => {
      rowsReq.expr = ExprUtils.whereParams('incDataSrcId', incDataSrcObj.id);
    },
    [incDataSrcObj.id],
  );

  // Conditions for every Rule of this Data Source are loaded once and grouped per Rule
  const { refreshTrigger: ruleRefreshTrigger } = useEntityChangeSubscr(module, 'INC_RULE');
  const { refreshTrigger: condRefreshTrigger } = useEntityChangeSubscr(module, 'INC_COND');
  useEffect(() => {
    if (!incDataSrcObj.id) return;
    rsrc
      .rows(module, 'INC_RULE', { expr: ExprUtils.whereParams('incDataSrcId', incDataSrcObj.id) })
      .then((rules) => {
        const ruleIds = (rules ?? []).map((rule) => rule.id);
        if (ruleIds.length === 0) return [];
        return rsrc.rows(module, 'INC_COND', { expr: ExprUtils.whereParams('incRuleId', ruleIds) });
      })
      .then((conds) => {
        const grouped = {};
        (conds ?? []).forEach((cond) => {
          (grouped[cond.incRuleId] ??= []).push(cond);
        });
        setRuleIdToConds(grouped);

        // Referenced lists are loaded so their items can be previewed without leaving the page
        const incListIds = [...new Set((conds ?? []).map((cond) => cond.incListId).filter(Boolean))];
        if (incListIds.length === 0) return;
        rsrc.rows(module, 'INC_LIST', { expr: ExprUtils.whereParams('id', incListIds) }).then((incLists) => {
          setIncListById(Object.fromEntries((incLists ?? []).map((incList) => [incList.id, incList])));
        });
      });
  }, [incDataSrcObj.id, ruleRefreshTrigger, condRefreshTrigger]);

  const openRuleDialog = (mode, rule) => {
    openDialog(EntityFormDialog, {
      title: `${mode === EntityFormMode.ADD ? 'Add' : 'Edit'} Rule — ${incDataSrcObj.code}`,
      module,
      entity: 'INC_RULE',
      mode,
      entityId: rule?.id,
      initialValues: mode === EntityFormMode.ADD ? { incDataSrcId: incDataSrcObj.id, inclusionary: true } : undefined,
      allowAddAnother: mode === EntityFormMode.ADD,
      maxWidth: 'md',
    }).then((saved) => {
      if (!saved) return;
      const last = Array.isArray(saved) ? saved[saved.length - 1] : saved;
      setHighlightId(last?.id ?? null);
    });
  };

  const openCondDialog = (mode, rule, cond) => {
    openDialog(EntityFormDialog, {
      title: `${mode === EntityFormMode.ADD ? 'Add' : 'Edit'} Condition — ${rule.code}`,
      module,
      entity: 'INC_COND',
      mode,
      entityId: cond?.id,
      initialValues: mode === EntityFormMode.ADD ? { incRuleId: rule.id } : undefined,
      allowAddAnother: mode === EntityFormMode.ADD,
      maxWidth: 'md',
      renderForm: (formProps) => <IncCondForm {...formProps} />,
    }).then((saved) => {
      if (saved) setHighlightId(rule.id);
    });
  };

  // Same right-click pattern as grid rows: Details, then Delete when permitted
  const contextMenuItems = (entity, row, canDelete) => {
    const items = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () =>
          navigateTo(`${module}/${entity === 'INC_RULE' ? 'inc-rule' : 'inc-cond'}`, {
            mode: Mode.DETAIL,
            entityId: row.id,
          }),
      },
    ];
    if (canDelete) {
      items.push({
        name: 'Delete',
        icon: fas.faTrash,
        disabled: LOCKED_STATUSES.includes(row.status),
        action: () => openDialog(ConfirmDeleteDialog, { data: { module, entity, id: row.id } }),
      });
    }
    return items;
  };

  const condValue = (cond) => {
    if (cond.value != null && cond.value !== '') return cond.value;
    if (!cond.incListId) return '—';
    const incList = incListById[cond.incListId];
    const items = incList?.items ? incList.items.split(',') : [];
    return (
      <Tooltip
        placement="right"
        title={
          items.length === 0 ?
            ''
          : <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              gap={0.5}
              sx={{ maxWidth: 420, maxHeight: 300, overflow: 'auto', p: 0.5 }}
            >
              {items.map((item, idx) => (
                <Chip key={idx} size="small" label={item} />
              ))}
            </Stack>
        }
      >
        <Link
          component="button"
          variant="body2"
          onClick={(event) => {
            event.stopPropagation();
            navigateTo(module + '/inc-list', { mode: Mode.DETAIL, entityId: cond.incListId });
          }}
        >
          {incList?.name ?? cond.incListName ?? `List #${cond.incListId}`}
          {items.length > 0 && ` (${items.length})`}
        </Link>
      </Tooltip>
    );
  };

  const renderSummary = (rule) => {
    const conds = ruleIdToConds[rule.id] ?? [];
    return (
      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
          <Typography fontWeight={600} noWrap>
            {rule.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {rule.code}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={rule.inclusionary ? 'success' : 'error'}
          variant="outlined"
          label={rule.inclusionary ? 'Inclusionary' : 'Exclusionary'}
        />
        <Chip size="small" variant="outlined" label={`${conds.length} condition${conds.length === 1 ? '' : 's'}`} />
        {rule.valStatus && rule.valStatus !== 'VALID' && rule.valStatus !== 'NONE' && (
          <Chip size="small" color="warning" label={rule.valStatus} />
        )}
        <Stack direction="row" sx={{ ml: 'auto' }} onClick={(event) => event.stopPropagation()}>
          {canSaveRule && (
            <Tooltip title="Edit rule">
              <IconButton size="small" onClick={() => openRuleDialog(EntityFormMode.EDIT, rule)}>
                <FontAwesomeIcon icon={fas.faPencil} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderBody = (rule) => {
    const conds = ruleIdToConds[rule.id] ?? [];
    if (conds.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No conditions yet. A rule without conditions matches every line.
        </Typography>
      );
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Transaction Column</TableCell>
            <TableCell>Operator</TableCell>
            <TableCell>Value</TableCell>
            {canSaveCond && <TableCell align="right" sx={{ width: 56 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {conds.map((cond) => (
            <TableRow
              key={cond.id}
              hover
              sx={canSaveCond ? { cursor: 'pointer' } : undefined}
              onClick={canSaveCond ? () => openCondDialog(EntityFormMode.EDIT, rule, cond) : undefined}
              onContextMenu={(event) => openContextMenu(event, contextMenuItems('INC_COND', cond, canDeleteCond))}
            >
              <TableCell>{cond.name}</TableCell>
              <TableCell>{StringUtils.toCapitalizedWords(cond.trxCol)}</TableCell>
              <TableCell>
                <EnumChip enumSpec={condEnumSpec('operator', cond.operator)} code={cond.operator} />
              </TableCell>
              <TableCell>{condValue(cond)}</TableCell>
              {canSaveCond && (
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Edit condition">
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        openCondDialog(EntityFormMode.EDIT, rule, cond);
                      }}
                    >
                      <FontAwesomeIcon icon={fas.faPencil} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderActions = (rule) => {
    if (!canSaveCond) return null;
    return (
      <Button
        size="small"
        startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
        onClick={() => openCondDialog(EntityFormMode.ADD, rule)}
      >
        Add condition
      </Button>
    );
  };

  const headerButtons = canSaveRule && (
    <Button
      variant="contained"
      size="small"
      startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
      onClick={() => openRuleDialog(EntityFormMode.ADD)}
    >
      Add rule
    </Button>
  );

  const emptyState = (
    <Stack spacing={2} alignItems="center">
      <Typography color="text.secondary">
        {canEdit ? 'No rules yet. Add rules to this data source.' : 'This data source has no rules.'}
      </Typography>
      {canSaveRule && (
        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
          onClick={() => openRuleDialog(EntityFormMode.ADD)}
        >
          Add rule
        </Button>
      )}
    </Stack>
  );

  return (
    <Stack>
      <EntityCardList
        module={module}
        entity="INC_RULE"
        title="Rules"
        augRowsReq={augRowsReq}
        refreshTrigger={condRefreshTrigger}
        renderSummary={renderSummary}
        renderBody={renderBody}
        renderActions={renderActions}
        hideSort
        contextMenuItems={(row) => contextMenuItems('INC_RULE', row, canDeleteRule)}
        headerButtons={headerButtons}
        emptyState={emptyState}
        highlightId={highlightId}
      />
      {condContextMenu}
    </Stack>
  );
};
